import type { CSSProperties, HTMLAttributes } from 'react'
import { cn } from './cn'

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** circle = round, text = 12px bar with a pill radius, block = rounded rectangle (default). */
  shape?: 'block' | 'circle' | 'text'
  width?: number | string
  height?: number | string
}

/** Loading placeholder: surface-2 with a subtle shimmer (static when reduced motion is on). */
export function Skeleton({ shape = 'block', width, height, className, style, ...rest }: SkeletonProps) {
  const dims: CSSProperties = { width, height, ...style }
  return (
    <div
      aria-hidden="true"
      className={cn(
        'mgs-shimmer animate-shimmer bg-surface-2 bg-[length:200%_100%]',
        'bg-[linear-gradient(90deg,transparent_0%,var(--mgs-shimmer)_50%,transparent_100%)]',
        shape === 'circle' && 'rounded-full',
        shape === 'text' && 'h-3 rounded-full',
        shape === 'block' && 'rounded-control',
        className,
      )}
      style={dims}
      {...rest}
    />
  )
}

/** A few text bars of decreasing width. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} shape="text" style={{ width: i === lines - 1 ? '60%' : i % 2 ? '88%' : '100%' }} />
      ))}
    </div>
  )
}
